/**
 * 静态 USDT 兑付 — 主号可代子账户申请；列表展示全部白名单名额
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { staticUsdtPayoutAPI, type StaticUsdtSlot } from '../api/static-usdt-payout';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';

export function StaticUsdtPayoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const meQuery = useQuery({
    queryKey: ['static-usdt-payout', 'me', 'household'],
    queryFn: () => staticUsdtPayoutAPI.me(),
  });

  // History is optional; don't block first paint (me already includes pending_requests).
  const listQuery = useQuery({
    queryKey: ['static-usdt-payout', 'requests'],
    queryFn: () => staticUsdtPayoutAPI.listRequests(),
    enabled: Boolean(meQuery.data),
  });

  const me = meQuery.data;
  const historyRows =
    listQuery.data?.rows ??
    (me?.pending_requests as Array<Record<string, unknown>> | undefined) ??
    [];
  const slots = me?.slots ?? [];
  const selected =
    slots.find((s) => s.allowlist_id === selectedId) ??
    slots.find((s) => s.can_apply) ??
    slots.find((s) => toNum(s.remaining_usd) > 0) ??
    slots[0] ??
    null;

  const createMut = useMutation({
    mutationFn: (slot: StaticUsdtSlot) =>
      staticUsdtPayoutAPI.create({
        amount_usd: slot.remaining_usd,
        allowlist_id: slot.allowlist_id,
        for_user_id: slot.user_id,
        transaction_password: txPassword,
      }),
    onSuccess: () => {
      setError(null);
      setTxPassword('');
      void qc.invalidateQueries({ queryKey: ['static-usdt-payout'] });
      void qc.invalidateQueries({ queryKey: ['user', 'balances'] });
      alert(t('staticUsdtPayout.submitOk', '申请已提交，等待审核'));
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('staticUsdtPayout.submitFail', '提交失败'));
    },
  });

  const applyAllMut = useMutation({
    mutationFn: () => staticUsdtPayoutAPI.applyAll({ transaction_password: txPassword }),
    onSuccess: (data) => {
      setError(null);
      setTxPassword('');
      void qc.invalidateQueries({ queryKey: ['static-usdt-payout'] });
      void qc.invalidateQueries({ queryKey: ['user', 'balances'] });
      alert(
        t('staticUsdtPayout.applyAllOk', '一键申请完成：成功 {{ok}} / 失败 {{fail}}', {
          ok: data.ok_count,
          fail: data.fail_count,
        }),
      );
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('staticUsdtPayout.submitFail', '提交失败'));
    },
  });

  const cancelMut = useMutation({
    mutationFn: (id: number) => staticUsdtPayoutAPI.cancel(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['static-usdt-payout'] });
      void qc.invalidateQueries({ queryKey: ['user', 'balances'] });
    },
    onError: (e: unknown) => {
      alert(e instanceof ApiError ? e.message : t('staticUsdtPayout.cancelFail', '取消失败'));
    },
  });

  if (meQuery.isLoading) {
    return (
      <div className="min-h-full pb-8" style={{ background: C.pageBg }}>
        <ThemedPageHeader
          title={t('staticUsdtPayout.title', '静态 USDT 兑付')}
          onBack={() => navigate(-1)}
          theme={C}
        />
        <div className="px-5 pt-20 text-sm" style={{ color: C.muted }}>
          {t('common.loading', '加载中…')}
        </div>
      </div>
    );
  }

  if (!me?.eligible || slots.length === 0) {
    return (
      <div className="min-h-full pb-8" style={{ background: C.pageBg }}>
        <ThemedPageHeader
          title={t('staticUsdtPayout.title', '静态 USDT 兑付')}
          onBack={() => navigate(-1)}
          theme={C}
        />
        <div className="px-5 pt-20">
          <ThemedNotice tone="info">
            {t(
              'staticUsdtPayout.notEligibleHousehold',
              '主号及子账户均不在兑付白名单内。',
            )}
          </ThemedNotice>
        </div>
      </div>
    );
  }

  const applyable = slots.filter((s) => s.can_apply);
  const busy = createMut.isPending || applyAllMut.isPending;

  function blockReason(slot: StaticUsdtSlot | null): string | null {
    if (!slot) return t('staticUsdtPayout.pickSlot', '请选择名额');
    if (toNum(slot.remaining_usd) <= 0) {
      return t('staticUsdtPayout.noRemaining', '该名额额度已用完');
    }
    if (!slot.wallet_address) {
      return t(
        'staticUsdtPayout.needWallet',
        '请先绑定主号或子账户 BEP20 钱包',
      );
    }
    if (!slot.can_apply) {
      return t(
        'staticUsdtPayout.needEp1',
        'EP1 不足（子账户与主号合计不够本笔额度）',
      );
    }
    return null;
  }

  function onSubmitOne() {
    setError(null);
    if (!txPassword.trim()) {
      setError(t('common.enterTxPassword', '请输入交易密码'));
      return;
    }
    const reason = blockReason(selected);
    if (reason || !selected) {
      setError(reason);
      return;
    }
    createMut.mutate(selected);
  }

  function onSubmitAll() {
    setError(null);
    if (!txPassword.trim()) {
      setError(t('common.enterTxPassword', '请输入交易密码'));
      return;
    }
    if (applyable.length === 0) {
      setError(
        t(
          'staticUsdtPayout.noneApplyable',
          '没有可申请名额（需剩余额度；可用主号钱包/EP1）',
        ),
      );
      return;
    }
    applyAllMut.mutate();
  }

  return (
    <div className="relative min-h-full pb-28" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('staticUsdtPayout.title', '静态 USDT 兑付')}
          onBack={() => navigate(-1)}
          theme={C}
        />

        <main className="space-y-4 px-5 pt-[70px]">
          <ThemedNotice tone="info">
            {t(
              'staticUsdtPayout.householdHint',
              '主号可代白名单子账户申请。子账户未绑钱包或 EP1 不足时，可用主号钱包收款并扣主号 EP1。',
            )}
          </ThemedNotice>

          <ThemedPanel className="p-3" theme={C}>
            <div className="mb-2 text-xs font-semibold" style={{ color: C.text }}>
              {t('staticUsdtPayout.slots', '白名单名额')}（{slots.length}）
            </div>
            <div className="space-y-2">
              {slots.map((slot) => {
                const active = selected?.allowlist_id === slot.allowlist_id;
                const rem = toNum(slot.remaining_usd);
                return (
                  <button
                    key={slot.allowlist_id}
                    type="button"
                    onClick={() => {
                      setSelectedId(slot.allowlist_id);
                      setError(null);
                    }}
                    className="w-full rounded-lg border px-3 py-2 text-left text-xs"
                    style={{
                      borderColor: active ? C.gold : C.softBorder,
                      background: active ? `${C.gold}14` : 'transparent',
                      color: C.text,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">
                        {slot.username}
                        {slot.is_sub
                          ? ` (${t('staticUsdtPayout.sub', '子账户')})`
                          : ` (${t('staticUsdtPayout.main', '主号')})`}
                      </span>
                      <span style={{ color: rem > 0 ? C.gold : C.muted }}>
                        ${slot.remaining_usd}
                      </span>
                    </div>
                    <div className="mt-1" style={{ color: C.muted }}>
                      {t('staticUsdtPayout.quota', '额度')} ${slot.quota_usd}
                      {' · '}EP1 {fmtEp1(slot.ep1_balance)}
                      {slot.ep1_from_master
                        ? ` · ${t('staticUsdtPayout.useMasterEp1', '扣主号EP1')}`
                        : null}
                      {slot.wallet_address
                        ? ` · ${shortWallet(slot.wallet_address)}${
                            slot.wallet_from_master
                              ? ` (${t('staticUsdtPayout.useMasterWallet', '主号钱包')})`
                              : ''
                          }`
                        : ` · ${t('staticUsdtPayout.noWallet', '未绑钱包')}`}
                    </div>
                  </button>
                );
              })}
            </div>
          </ThemedPanel>

          {selected && (
            <ThemedPanel className="p-4" theme={C}>
              <div className="text-sm font-semibold" style={{ color: C.text }}>
                {selected.username}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                <div>
                  <div style={{ color: C.muted }}>{t('staticUsdtPayout.quota', '额度')}</div>
                  <div style={{ color: C.text }}>${selected.quota_usd}</div>
                </div>
                <div>
                  <div style={{ color: C.muted }}>{t('staticUsdtPayout.used', '已用')}</div>
                  <div style={{ color: C.text }}>${selected.used_usd}</div>
                </div>
                <div>
                  <div style={{ color: C.muted }}>{t('staticUsdtPayout.remaining', '剩余')}</div>
                  <div style={{ color: C.gold }}>${selected.remaining_usd}</div>
                </div>
              </div>
              {selected.can_apply ? (
                <div className="mt-3 text-[11px]" style={{ color: C.buy }}>
                  {selected.wallet_from_master || selected.ep1_from_master
                    ? t(
                        'staticUsdtPayout.readyMasterCover',
                        '可申请：将使用主号钱包/EP1 代扣',
                      )
                    : t('staticUsdtPayout.ready', '可申请')}
                </div>
              ) : (
                <div className="mt-3 text-[11px]" style={{ color: C.sell }}>
                  {blockReason(selected)}
                </div>
              )}
            </ThemedPanel>
          )}

          <ThemedNotice tone="info">
            {t(
              'staticUsdtPayout.ep1Hint',
              '提交后将立即扣除等额 EP1；审核拒绝或取消后退回。',
            )}
          </ThemedNotice>

          <TransactionSecurity
            txPassword={txPassword}
            onTxPasswordChange={setTxPassword}
            showSecurity={showSecurity}
            onShowSecurityChange={setShowSecurity}
            securityAnswer={securityAnswer}
            onSecurityAnswerChange={setSecurityAnswer}
            securityQuestionText={t('withdraw.defaultSecurityQuestion', '密保问题')}
            disabled={busy}
            colors={{
              muted: C.muted,
              red: C.sell,
              activeGreen: C.buy,
              gold: C.gold,
              softBorder: C.softBorder,
              inputBg: C.inputBg,
            }}
          />

          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}

          <ThemedPanel className="p-3" theme={C}>
            <div className="mb-2 text-xs font-semibold" style={{ color: C.text }}>
              {t('staticUsdtPayout.history', '申请记录')}
            </div>
            <div className="space-y-2">
              {historyRows.length === 0 && (
                <div className="text-xs" style={{ color: C.muted }}>
                  {t('staticUsdtPayout.noHistory', '暂无记录')}
                </div>
              )}
              {historyRows.map((row) => {
                const id = Number(row.id);
                const status = String(row.status);
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs"
                    style={{ borderColor: C.softBorder, color: C.text }}
                  >
                    <span>
                      {String(row.username ?? '')} #{id} · ${String(row.amount_usd)} · {status}
                    </span>
                    {status === 'pending' && (
                      <button
                        type="button"
                        className="underline"
                        style={{ color: C.sell }}
                        disabled={cancelMut.isPending}
                        onClick={() => cancelMut.mutate(id)}
                      >
                        {t('common.cancel', '取消')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </ThemedPanel>
        </main>

        <ThemedBottomActionBar background={C.bottomBarBg} className="pb-5 pt-3">
          <div className="flex w-full flex-col gap-2">
            <ThemedSubmitButton disabled={busy} onClick={onSubmitOne}>
              {createMut.isPending
                ? t('common.processing', '处理中…')
                : t('staticUsdtPayout.submitOne', '申请所选账号（扣 EP1）')}
            </ThemedSubmitButton>
            <ThemedSubmitButton disabled={busy} onClick={onSubmitAll}>
              {applyAllMut.isPending
                ? t('common.processing', '处理中…')
                : t('staticUsdtPayout.submitAll', '一键全部申请（{{n}}）', {
                    n: applyable.length,
                  })}
            </ThemedSubmitButton>
          </div>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}

function toNum(v: string | number | null | undefined): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtEp1(v: string): string {
  const n = toNum(v);
  if (n === 0) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function shortWallet(addr: string): string {
  if (addr.length < 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}
